import React, { createContext, useContext, useState, ReactNode } from 'react';
import { HistoryItem } from '../utils/historyManager';

interface EditorState {
  inputText: string;
  outputText: string;
  isEditingFromHistory: boolean;
  editingHistoryItem: HistoryItem | null;
  hasUnsavedChanges: boolean;
}

interface EditorContextType {
  editorState: EditorState;
  setInputText: (text: string) => void;
  setOutputText: (text: string) => void;
  loadFromHistory: (historyItem: HistoryItem) => void;
  clearEditor: () => void;
  markAsSaved: () => void;
  markAsUnsaved: () => void;
}

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
};

interface EditorProviderProps {
  children: ReactNode;
}

export const EditorProvider: React.FC<EditorProviderProps> = ({ children }) => {
  const [editorState, setEditorState] = useState<EditorState>({
    inputText: '',
    outputText: '',
    isEditingFromHistory: false,
    editingHistoryItem: null,
    hasUnsavedChanges: false,
  });

  const setInputText = (text: string) => {
    setEditorState(prev => ({
      ...prev,
      inputText: text,
      hasUnsavedChanges: prev.isEditingFromHistory && text !== prev.editingHistoryItem?.rewrittenText,
    }));
  };

  const setOutputText = (text: string) => {
    setEditorState(prev => ({
      ...prev,
      outputText: text,
      hasUnsavedChanges: prev.isEditingFromHistory && text !== prev.editingHistoryItem?.rewrittenText,
    }));
  };

  const loadFromHistory = (historyItem: HistoryItem) => {
    setEditorState({
      inputText: historyItem.originalText,
      outputText: historyItem.rewrittenText,
      isEditingFromHistory: true,
      editingHistoryItem: historyItem,
      hasUnsavedChanges: false,
    });
  };

  const clearEditor = () => {
    setEditorState({
      inputText: '',
      outputText: '',
      isEditingFromHistory: false,
      editingHistoryItem: null,
      hasUnsavedChanges: false,
    });
  };

  const markAsSaved = () => {
    setEditorState(prev => ({
      ...prev,
      hasUnsavedChanges: false,
      isEditingFromHistory: false,
      editingHistoryItem: null,
    }));
  };

  const markAsUnsaved = () => {
    setEditorState(prev => ({
      ...prev,
      hasUnsavedChanges: true,
    }));
  };

  const value: EditorContextType = {
    editorState,
    setInputText,
    setOutputText,
    loadFromHistory,
    clearEditor,
    markAsSaved,
    markAsUnsaved,
  };

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
};